import React, { useEffect, useState } from "react";
import { Authorization } from "../../Context/Authorization";
import {
    BarChart,
    Bar,
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
} from "recharts";
import Preloader from "../../shared/PreLoaders";
const thStyle = { padding: "10px 12px", fontSize: 12, fontWeight: 700, color: "#2D3748", background: "#F7FAFC", border: "1px solid #E2E8F0", textAlign: "left" };
const tdStyle = { padding: "9px 12px", fontSize: 12, color: "#4A5568", border: "1px solid #E2E8F0" };

const EcoTourismDashboard = () => {
    const [dashboardData, setDashboardData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedMonth, setSelectedMonth] = useState(() => {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, "0");
        return `${year}-${month}`;
    });
    const Api = process.env.REACT_APP_API;

    const fetchDashboardData = async () => {
        try {
            setLoading(true);

            const config = Authorization();

            const response = await fetch(
                `${Api}/api/eco-tourism-table/eco-dashboard`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        ...config.headers,
                    },
                    body: JSON.stringify({
                        month: selectedMonth,
                    }),
                }
            );

            const data = await response.json();
            setDashboardData(data);
        } catch (error) {
            console.error("Error fetching dashboard:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDashboardData();
    }, [selectedMonth]);

    const formatCurrency = (value) =>
        `₹${Number(value || 0).toLocaleString("en-IN")}`;

    const COLORS = [
        "#0088FE",
        "#00C49F",
        "#FFBB28",
        "#FF8042",
        "#A28BFE",
        "#F06292",
        "#4DB6AC",
        "#9575CD",
    ];

    if (loading) {
        return (
            <div className="text-center mt-5">
                <Preloader loading={loading} />
            </div>
        );
    }

    if (!dashboardData?.success) {
        return (
            <div className="text-center mt-5">
                <Preloader />
            </div>
        );
    }

    const { totals, divisionWise, revenueGraph } = dashboardData;

    return (
        <>
            <div className="page-header">
                <h3 className="page-title">
                    <span className="page-title-icon bg-gradient-primary text-white mr-2">
                        <i className="mdi mdi-home" style={{ color: "#fff" }}></i>
                    </span>
                    Dashboard
                </h3>
            </div>
            <div className="dashboard-sections">
                {/* Summary Cards */}
                <div className="jfmc-section">
                    <h4 className="jfmc-title mb-0">Eco Tourism Dashboard</h4>
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <div style={{ width: "250px" }}>
                            <label className="form-label fw-bold">Select Month</label>
                            <input
                                type="month"
                                className="form-control"
                                value={selectedMonth}
                                onChange={(e) => setSelectedMonth(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="jfmc-row mt-3">

                        {/* VFCs */}
                        <div className="stat-card card-green stat-centered">
                            <div className="stat-icon-big icon-green"> <i className="mdi mdi-nature"></i></div>
                            <div className="stat-title text-center">Total Eco Tourism</div>
                            <div className="stat-value text-center">
                                {totals.totalCenters}
                            </div>
                            <div className="stat-sub text-center">Active Centers</div>
                        </div>

                        {/* Members */}
                        <div className="stat-card card-blue stat-centered">
                            <div className="stat-icon-big icon-blue">  <i className="fas fa-rupee-sign"></i> </div>
                            <div className="stat-title text-center">Total Revenue</div>
                            <div className="stat-value text-center">
                                {formatCurrency(totals.totalRevenue)}
                            </div>
                            <div className="stat-sub text-center">Revenue Collected</div>
                        </div>

                        {/* SHGs */}
                        <div className="stat-card card-orange stat-centered">
                            <div className="stat-icon-big icon-orange"> <i className="fas fa-hiking"></i> </div>
                            <div className="stat-title text-center">Total Visitors</div>
                            <div className="stat-value text-center">{totals.totalVisitors}</div>
                            <div className="stat-sub text-center">Visitors</div>
                        </div>


                        {/* Number of Loans */}
                        <div className="stat-card card-purple stat-centered">
                            <div className="stat-icon-big icon-purple"> <i className="fas fa-user-tie"></i> </div>
                            <div className="stat-title text-center">Number EDC Workers</div>
                            <div className="stat-value text-center">{totals.totalEdcWorkers}</div>
                            <div className="stat-sub text-center">Active Workers</div>
                        </div>
                        {/* Bank Balance */}
                        <div className="stat-card card-red stat-centered">
                            <div className="stat-icon-big icon-red"> <i className="fas fa-bed"></i> </div>
                            <div className="stat-title text-center">Total Rooms Occupied</div>
                            <div className="stat-value text-center">
                                {totals.totalRoomsOccupied}
                            </div>
                            <div className="stat-sub text-center">Active Rooms</div>
                        </div>

                    </div>
                </div>

                <div className="dash-section-header mt-4">
                    <i className="mdi mdi-chart-bar dash-section-icon" />
                    <span>Division-wise Analytics</span>
                </div>

                {/* Charts */}
                <div className="row g-4">
                    {/* Revenue Trend */}
                    <div className="card dash-chart-card flex-fill d-flex flex-column">
                        <div className="card-header dash-chart-header">
                            <span className="dash-chart-icon dash-icon-blue">
                                <i className="mdi mdi-account-group" />
                            </span>
                            <div>
                                <div className="dash-chart-title">EDC Workers by Division</div>
                                <div className="dash-chart-sub">Combined view</div>
                            </div>
                        </div>

                        <div className="card-body dash-chart-body flex-grow-1">
                            <ResponsiveContainer width="100%" height={400}>
                                <BarChart data={dashboardData.edcWorkersGraph}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis
                                        dataKey="division"
                                        angle={-25}
                                        textAnchor="end"
                                        height={80}
                                    />
                                    <YAxis />
                                    <Tooltip />
                                    <Bar
                                        dataKey="workers"
                                        fill="#6f42c1"
                                        radius={[8, 8, 0, 0]}
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                    {/* Division Revenue Pie */}
                    <div className="col-md-6">

                        <div className="card dash-chart-card flex-fill d-flex flex-column">
                            <div className="card-header dash-chart-header">
                                <span className="dash-chart-icon dash-icon-blue">
                                    <i className="mdi mdi-account-group" />
                                </span>
                                <div>
                                    <div className="dash-chart-title">Visitors by Division</div>
                                    <div className="dash-chart-sub">Combined view</div>
                                </div>
                            </div>

                            <div className="card-body dash-chart-body flex-grow-1">
                                <ResponsiveContainer width="100%" height={350}>
                                    <PieChart>
                                        <Pie
                                            data={divisionWise}
                                            dataKey="totalVisitors"
                                            nameKey="division"
                                            outerRadius={120}
                                            label
                                        >
                                            {divisionWise.map((entry, index) => (
                                                <Cell
                                                    key={index}
                                                    fill={COLORS[index % COLORS.length]}
                                                />
                                            ))}
                                        </Pie>
                                        <Tooltip />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </div>
                    {/* Division Revenue Bar */}
                    <div className="col-md-6">

                        <div className="card dash-chart-card flex-fill d-flex flex-column">
                            <div className="card-header dash-chart-header">
                                <span className="dash-chart-icon dash-icon-blue">
                                    <i className="mdi mdi-account-group" />
                                </span>
                                <div>
                                    <div className="dash-chart-title">Division-wise Revenue</div>
                                    <div className="dash-chart-sub">Combined view</div>
                                </div>
                            </div>

                            <div className="card-body dash-chart-body flex-grow-1">
                                <ResponsiveContainer width="100%" height={350}>
                                    <BarChart data={dashboardData.divisionRevenueGraph}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis
                                            dataKey="division"
                                            angle={-25}
                                            textAnchor="end"
                                            height={80}
                                        />
                                        <YAxis />
                                        <Tooltip formatter={(value) => formatCurrency(value)} />
                                        <Bar
                                            dataKey="revenue"
                                            fill="#0d6efd"
                                            radius={[8, 8, 0, 0]}
                                        />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </div>

                    <div className="col-md-12">
                        <div className="card dash-chart-card">
                            <div className="card-header dash-chart-header">
                                <span className="dash-chart-icon dash-icon-blue">
                                    <i className="mdi mdi-chart-line" />
                                </span>

                                <div>
                                    <div className="dash-chart-title">
                                        Daily Revenue Trend
                                    </div>

                                    <div className="dash-chart-sub">
                                        Revenue collected per day
                                    </div>
                                </div>
                            </div>

                            <div className="card-body">
                                <ResponsiveContainer width="100%" height={350}>
                                    <LineChart data={revenueGraph}>
                                        <CartesianGrid strokeDasharray="3 3" />

                                        <XAxis
                                            dataKey="date"
                                            angle={-45}
                                            textAnchor="end"
                                            height={80}
                                        />

                                        <YAxis />

                                        <Tooltip
                                            formatter={(value) => formatCurrency(value)}
                                        />

                                        <Line
                                            type="monotone"
                                            dataKey="revenue"
                                            stroke="#0d6efd"
                                            strokeWidth={3}
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </div>

                </div>


                {/* Table */}
                <div className="card shadow-sm border-0">
                    <div className="card-body">
                        <h5 className="mb-3">Division-wise Summary</h5>

                        <div style={{ padding: 16, overflowX: "auto" }}>
                            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                <thead className="table-dark">
                                    <tr>
                                        <th style={thStyle}>Division</th>
                                        <th style={thStyle}>Centers</th>
                                        <th style={thStyle}>Revenue</th>
                                        <th style={thStyle}>Visitors</th>
                                        <th style={thStyle}>Rooms</th>
                                        <th style={thStyle}>EDC Workers</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {divisionWise.map((item, index) => (
                                        <tr key={index}>
                                            <td style={{ ...tdStyle, fontWeight: 600, color: "#2D3748" }}>{item.division}</td>
                                            <td style={{ ...tdStyle, fontWeight: 600, color: "#2D3748" }}>{item.totalCenters}</td>
                                            <td style={tdStyle}>
                                                {formatCurrency(item.totalRevenue)}
                                            </td>
                                            <td style={tdStyle}>{item.totalVisitors}</td>
                                            <td style={tdStyle}>{item.totalRoomsOccupied}</td>
                                            <td style={tdStyle}>{item.totalEdcWorkers}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <div className="card shadow-sm border-0">
                    <div className="card-body">
                        <h5 className="mb-3">Division-wise Summary</h5>

                        <div style={{ padding: 16, overflowX: "auto" }}>
                            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                <thead className="table-dark">
                                    <tr>
                                        <th style={thStyle}>Division</th>

                                        <th style={thStyle}>
                                            Eco Tourism Centers
                                        </th>

                                        <th style={thStyle}>Centers</th>

                                        <th style={thStyle}>Revenue</th>

                                        <th style={thStyle}>Visitors</th>

                                        <th style={thStyle}>Rooms</th>

                                        <th style={thStyle}>EDC Workers</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {divisionWise.map((item, index) => (
                                        <tr key={index}>
                                            {/* Division */}
                                            <td
                                                style={{
                                                    ...tdStyle,
                                                    fontWeight: 600,
                                                    color: "#2D3748",
                                                }}
                                            >
                                                {item.division}
                                            </td>

                                            {/* Eco Tourism Centers */}
                                            <td style={tdStyle}>
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        gap: 6,
                                                        minWidth: 250,
                                                    }}
                                                >
                                                    {item.ecoTourismCenters?.map((eco) => (
                                                        <div
                                                            key={eco._id}
                                                            style={{
                                                                padding: "4px 8px",
                                                                background: "#F0FFF4",
                                                                border: "1px solid #C6F6D5",
                                                                borderRadius: 6,
                                                                color: "#1B5E20",
                                                                fontWeight: 500,
                                                                fontSize: 12,
                                                            }}
                                                        >
                                                            {eco.ecoTourismName}
                                                        </div>
                                                    ))}
                                                </div>
                                            </td>

                                            {/* Total Centers */}
                                            <td
                                                style={{
                                                    ...tdStyle,
                                                    fontWeight: 600,
                                                    color: "#2D3748",
                                                    textAlign: "center",
                                                }}
                                            >
                                                {item.totalCenters}
                                            </td>

                                            {/* Revenue */}
                                            <td style={tdStyle}>
                                                {formatCurrency(item.totalRevenue)}
                                            </td>

                                            {/* Visitors */}
                                            <td style={tdStyle}>
                                                {item.totalVisitors}
                                            </td>

                                            {/* Rooms */}
                                            <td style={tdStyle}>
                                                {item.totalRoomsOccupied}
                                            </td>

                                            {/* Workers */}
                                            <td style={tdStyle}>
                                                {item.totalEdcWorkers}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default EcoTourismDashboard;